import React, { useCallback, useMemo } from "react"
import {
  Box,
  useTheme,
  Tabs,
  Tab,
  Typography,
  Stack,
  Button,
} from "@mui/material"
import _ from "lodash"
import { PageCard, PageCardContent } from "components/card"
import { AppNavigation } from "containers/AppNavigation"
import { NavigationControl } from "components/kioskHorizontal/navigation"
import { MotionDiv } from "components/motions"

import { useTranslation } from "providers/i18n"
import { useBusDetail } from "extensions/em-transportation/hooks/useBusDetail"

import { TransportationTabPanel } from "extensions/em-transportation/components/TransportationTabPanel"
import { BusStopCard } from "extensions/em-transportation/components/BusStopCard"
import { QRCode } from "components/QRCode"
import { BusLocationIcon } from "extensions/em-transportation/components/icons"
import { useNavigate } from "react-router-dom"
import { useExtension } from "hooks/extension/useExtension"

export const EmBusTransportationKioskHPage = ({ Layout = Box }) => {
  const navigate = useNavigate()
  const { t } = useTranslation()

  const { extensionConfig } = useExtension()
  const busConfig = useMemo(
    () => _.get(extensionConfig, "em-transportation.options"),
    [extensionConfig]
  )

  const inboundLocationLinks = useMemo(
    () => _.get(busConfig, "bus.inboundLocations", []),
    [busConfig]
  )
  const outboundLocationLinks = useMemo(
    () => _.get(busConfig, "bus.outboundLocations", []),
    [busConfig]
  )

  const {
    selectedTrafficType,
    changeTrafficType,
    trafficTypes,
    inbound,
    outbound,
  } = useBusDetail(inboundLocationLinks, outboundLocationLinks, busConfig)
  const {
    data: inboundData,
    locations: inboundLocations,
    mobileUrl: inboundMobileUrl,
  } = inbound
  const {
    data: outboundData,
    locations: outboundLocations,
    mobileUrl: outboundMobileUrl,
  } = outbound
  const theme = useTheme()

  const { styleOverrides: bannerStyleOverrides = {} } =
    theme.components[`AppBanner`] || {}
  const sideBarStyleOverrides =
    theme.components[`AppSidebar`]?.styleOverrides || {}
  const { styleOverrides: emTransportationPageStyleOverrides = {} } =
    theme.components[`EmTransportationPage`] || {}
  const { root: bannerRoot } = bannerStyleOverrides
  const { wrapper, body, footer } = sideBarStyleOverrides
  const { qrCode } = emTransportationPageStyleOverrides

  const styleOverrides =
    theme.components[`EmBusTransportationPage`]?.styleOverrides || {}
  const {
    root,
    pageCardContent,
    pageTitle,
    trafficTabs,
    trafficTab,
    trafficTabPanel,
    trafficTabContainer,
    button,
    busCardWrapper,
    qrCodeWrapper,
    buttonWrapper,
  } = styleOverrides

  const onChangeTab = useCallback(
    (e, trafficType) => {
      changeTrafficType(trafficType)
    },
    [changeTrafficType]
  )

  const onClickBusLocation = useCallback(
    (id) => {
      navigate(`/maps/place/${id}`)
    },
    [navigate]
  )

  return (
    <Layout
      aside={
        <PageCard>
          <Box sx={wrapper}>
            <Box sx={bannerRoot}></Box>
            <Box sx={body}>
              <AppNavigation />
            </Box>
            <Box sx={footer}>
              <NavigationControl />
            </Box>
          </Box>
        </PageCard>
      }
    >
      <PageCard sx={root}>
        <MotionDiv
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.15 }}
        >
          <PageCardContent sx={pageCardContent}>
            <Typography sx={pageTitle} variant="h2">
              {t("Bus")}
            </Typography>
            <Box sx={trafficTabContainer}>
              <Tabs
                sx={trafficTabs}
                value={selectedTrafficType}
                onChange={onChangeTab}
                variant="fullWidth"
              >
                <Tab
                  sx={trafficTab}
                  label={t("Inbound")}
                  value={trafficTypes.INBOUND}
                />
                <Tab
                  sx={trafficTab}
                  label={t("Outbound")}
                  value={trafficTypes.OUTBOUND}
                />
              </Tabs>
              <TransportationTabPanel
                sx={trafficTabPanel}
                value={selectedTrafficType}
                index={trafficTypes.INBOUND}
              >
                <Box sx={busCardWrapper}>
                  {inboundData.length > 0 &&
                    inboundData.map((data) => {
                      const { title, destination, origin, id } = data
                      return (
                        <BusStopCard
                          key={id}
                          title={title}
                          destination={destination}
                          origin={origin}
                        />
                      )
                    })}
                </Box>
                <Box sx={qrCodeWrapper}>
                  <QRCode
                    sx={qrCode}
                    caption={t("Send to Mobile")}
                    url={inboundMobileUrl}
                  />
                </Box>
                <Stack spacing={2.5} direction={"row"} sx={buttonWrapper}>
                  {inboundLocations.length > 0 &&
                    inboundLocations.map((data) => {
                      const { id, location, name } = data
                      return (
                        <Button
                          variant="contained"
                          startIcon={<BusLocationIcon />}
                          sx={button}
                          key={id}
                          onClick={() => onClickBusLocation(location?.id)}
                        >
                          {t(name)}
                        </Button>
                      )
                    })}
                </Stack>
              </TransportationTabPanel>
              <TransportationTabPanel
                sx={trafficTabPanel}
                value={selectedTrafficType}
                index={trafficTypes.OUTBOUND}
              >
                <Box sx={busCardWrapper}>
                  {outboundData.length > 0 &&
                    outboundData.map((data) => {
                      const { title, destination, origin, id } = data
                      return (
                        <BusStopCard
                          key={id}
                          title={title}
                          destination={destination}
                          origin={origin}
                        />
                      )
                    })}
                </Box>
                <Box sx={qrCodeWrapper}>
                  <QRCode
                    sx={qrCode}
                    caption={t("Send to Mobile")}
                    url={outboundMobileUrl}
                  />
                </Box>
                <Stack spacing={2.5} direction={"row"} sx={buttonWrapper}>
                  {outboundLocations.length > 0 &&
                    outboundLocations.map((data) => {
                      const { id, location, name } = data
                      return (
                        <Button
                          variant="contained"
                          startIcon={<BusLocationIcon />}
                          sx={button}
                          key={id}
                          onClick={() => onClickBusLocation(location?.id)}
                        >
                          {t(name)}
                        </Button>
                      )
                    })}
                </Stack>
              </TransportationTabPanel>
            </Box>
          </PageCardContent>
        </MotionDiv>
      </PageCard>
    </Layout>
  )
}
